import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, Stagger, StaggerItem, SectionHead, CtaGhost, PageHero, Check,
  RuleLabel, TextLink, AgentAvatar, BigStat, ClosingCta,
} from '../components/V2Shared';
import { AGENTS_FRONT, AGENTS_REVENUE, CLINICAL_AI } from '../data/site';

const ROSTERS = [
  {
    key: 'front',
    label: 'Front office',
    sub: 'Calls, scheduling, recall, intake, reputation',
    agents: AGENTS_FRONT,
    to: '/v2/front-office',
  },
  {
    key: 'revenue',
    label: 'Revenue cycle',
    sub: 'Verification, claims, posting, denials, A/R',
    agents: AGENTS_REVENUE,
    to: '/v2/revenue-cycle',
  },
];

/* roster directory with a selected-agent dossier panel */
const Roster = ({ group }) => {
  const [selected, setSelected] = useState(group.agents[0]);
  return (
    <div className="mt-10 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
      {/* directory list */}
      <div className="overflow-hidden rounded-2xl border border-[#dde5f3]">
        <div className="flex items-center justify-between bg-[#f6f9ff] px-5 py-3.5">
          <span className="v2-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7c89a8]">
            {group.label} — {group.agents.length} agents
          </span>
          <TextLink to={group.to}>Deep dive</TextLink>
        </div>
        {group.agents.map((a) => {
          const active = selected.name === a.name;
          return (
            <button
              key={a.name}
              onClick={() => setSelected(a)}
              className={`v2-row flex w-full items-center gap-4 px-5 py-3 text-left transition-colors ${
                active ? '!bg-[#eef3ff]' : ''
              }`}
            >
              <span className={`v2-mono w-12 shrink-0 text-[11px] font-medium ${active ? 'text-[#1a46e5]' : 'text-[#a8bcf0]'}`}>
                {a.tag}
              </span>
              <AgentAvatar name={a.name} img={a.img} size="h-9 w-9" />
              <div className="min-w-0">
                <div className={`text-[14.5px] font-bold ${active ? 'text-[#1a46e5]' : 'text-[#0a1633]'}`}>{a.name}</div>
                <div className="truncate text-[12.5px] text-[#7c89a8]">{a.role}</div>
              </div>
              <ArrowRight
                className={`ml-auto h-4 w-4 shrink-0 transition-all ${
                  active ? 'translate-x-0 text-[#1a46e5]' : '-translate-x-1 text-[#dde5f3]'
                }`}
              />
            </button>
          );
        })}
      </div>

      {/* dossier */}
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={selected.name}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="v2-window lg:sticky lg:top-32 overflow-hidden rounded-2xl"
          >
            <div className="v2-mono flex items-center justify-between border-b border-[#dde5f3] bg-[#f6f9ff] px-5 py-2.5 text-[10.5px] font-medium uppercase tracking-[0.12em] text-[#7c89a8]">
              <span>AI workforce — agent {selected.tag}</span>
              <span className="flex items-center gap-1.5 text-[#0f9d76]">
                <span className="v2-pulse h-1.5 w-1.5 rounded-full bg-[#0f9d76]" /> on shift
              </span>
            </div>
            <div className="p-7 md:p-8">
              <div className="flex items-center gap-5">
                <AgentAvatar name={selected.name} img={selected.img} size="h-16 w-16" />
                <div>
                  <div className="v2-display text-[28px] font-semibold leading-none text-[#0a1633]">{selected.name}</div>
                  <div className="v2-mono mt-2 text-[11.5px] font-medium uppercase tracking-[0.14em] text-[#1a46e5]">
                    {selected.role}
                  </div>
                </div>
              </div>
              <p className="mt-6 text-[15px] leading-relaxed text-[#3f4d6e]">{selected.detail}</p>
              <ul className="mt-6 space-y-2.5 border-t border-[#eef3ff] pt-6">
                {selected.points.map((pt) => (
                  <Check key={pt}>{pt}</Check>
                ))}
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

const V2AiTeam = () => (
  <V2Layout title="The AI Workforce — Asha">
    <PageHero
      eyebrow="The AI workforce / 20+ agents"
      title={<>Meet the team that <span className="text-[#1a46e5]">never clocks out.</span></>}
      body="Twenty-plus AI employees with names, jobs, and the authority to finish them — answering phones, filling chairs, verifying coverage, submitting claims, fighting denials. They run on one shared memory, so what one agent learns, every agent knows."
    >
      <CtaGhost to="/book-demo">Watch them work</CtaGhost>
      <TextLink to="/v2/aria">Powered by the ARIA engine</TextLink>
    </PageHero>

    {/* group portrait strip */}
    <section className="px-6 pb-6">
      <div className="mx-auto max-w-7xl">
        <FadeUp>
          <div className="flex flex-wrap items-center gap-y-3 rounded-2xl border border-[#dde5f3] bg-[#f6f9ff] px-6 py-5">
            <div className="flex -space-x-3">
              {[...AGENTS_FRONT, ...AGENTS_REVENUE].map((a) => (
                <AgentAvatar key={a.name} name={a.name} img={a.img} size="h-11 w-11" ring={false} />
              ))}
            </div>
            <div className="v2-mono ml-auto flex gap-x-6 text-[11.5px] font-medium uppercase tracking-[0.14em] text-[#7c89a8]">
              <span>16 named agents</span>
              <span>4 clinical ai products</span>
              <span>1 shared memory</span>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>

    {/* rosters */}
    {ROSTERS.map((group, gi) => (
      <section key={group.key} className="px-6 py-16 md:py-20">
        <div className="mx-auto max-w-7xl">
          <RuleLabel label={`${String(gi + 1).padStart(2, '0')} / ${group.label} agents`} />
          <div className="mt-10">
            <SectionHead
              eyebrow={group.sub}
              title={group.key === 'front'
                ? <>The front office that answers every call.</>
                : <>The billing department that never lets a claim age.</>}
            />
          </div>
          <Roster group={group} />
        </div>
      </section>
    ))}

    {/* clinical AI strip */}
    <section className="v2-canvas px-6 py-20 md:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHead
            eyebrow="03 / clinical ai"
            title={<>And four more in the operatory.</>}
            body="The Scribe, the Treatment Planner, Voice Perio, and the Copilot live inside the chart — clinical AI for the part of the day that actually involves teeth."
          />
          <FadeUp delay={0.15}>
            <CtaGhost to="/v2/clinical-ai">Explore Clinical AI</CtaGhost>
          </FadeUp>
        </div>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CLINICAL_AI.map((c) => (
            <StaggerItem key={c.name} className="h-full">
              <div className="v2-card h-full rounded-2xl p-7">
                <span className="v2-mono text-[11px] font-medium text-[#a8bcf0]">{c.tag}</span>
                <div className="v2-display mt-3 text-[20px] font-semibold leading-tight text-[#0a1633]">{c.name}</div>
                <p className="mt-3 text-[14px] leading-relaxed text-[#3f4d6e]">{c.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* shared memory panel */}
    <section className="px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <SectionHead
              dark
              eyebrow="one shared brain"
              title={<>What one agent learns, every agent knows.</>}
              body="Amanda's benefit breakdown is what Amy quotes on the phone, what Ariel submits against, and what Sarah cites in the appeal. Point-solution AI can't do this — its context dies at the edge of each app. Asha's agents work one patient record, together."
            />
            <FadeUp delay={0.12}>
              <div className="space-y-3">
                {[
                  { from: 'Amanda', to: 'Amy', what: 'live benefits quoted mid-call' },
                  { from: 'Amy', to: 'Leena', what: 'new booking placed in the right operatory' },
                  { from: 'Amanda', to: 'Ariel', what: 'claim built on verified coverage' },
                  { from: 'Ariel', to: 'Sarah', what: 'appeal cites the original submission' },
                  { from: 'Alisha', to: 'Kianna', what: 'statement reflects the posted EOB' },
                ].map((h) => (
                  <div key={`${h.from}-${h.to}`} className="flex items-center gap-4 rounded-xl border border-white/12 bg-white/5 px-5 py-3.5">
                    <span className="v2-mono w-20 shrink-0 text-[12px] font-semibold text-white">{h.from}</span>
                    <svg viewBox="0 0 48 8" className="h-2 w-12 shrink-0 text-[#9db4ff]">
                      <line x1="0" y1="4" x2="40" y2="4" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" className="v2-dash" />
                      <path d="M40 1l6 3-6 3z" fill="currentColor" />
                    </svg>
                    <span className="v2-mono w-20 shrink-0 text-[12px] font-semibold text-white">{h.to}</span>
                    <span className="hidden text-[13px] text-white/60 sm:block">{h.what}</span>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <div className="mt-16 grid grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="20+" label="agents on shift" />
              <BigStat dark value="40+" label="tasks they own" />
              <BigStat dark value="1" label="shared memory" />
              <BigStat dark value="0" label="dropped handoffs" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <ClosingCta
      title="Put the whole team on your next shift."
      body="A 30-minute demo, tailored to your practice — watch the agents work real calls, real claims, and a real schedule."
    />
  </V2Layout>
);

export default V2AiTeam;
